import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Form, Grid, Icon, Loader } from "semantic-ui-react";
import { createPayoffQuoteRequest, getPayoffQuoteLoanList } from "../../api/BorrowerApi";
import { createMockPayoffQuoteRequest, getMockPayoffQuoteLoanList } from "../../api/mock/MockBorrowerApi";
import { getDropdownOptions, handleErrorResponse, handleFormChange, validateEmail, validateObj } from "../../Helpers";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BCDashboard from "../ui/BCDashboard";
import BCFormInput from "../ui/BCFormInput";
import BCValidationMessage from "../ui/BCValidationMessage";
import "../../styles/borrower/vendor-form.scss";
import { IPayoffQuoteRequest } from "../interfaces/Borrower";
import BCMessage from "../ui/BCMessage";
import { FeatureFlags } from "../constants/LocalServerConstants";

interface IBWPayoffQuoteRequestForm { // TODO: edit interface after getting end point
    isGreaterThanMobile: boolean;
    isGreaterThanTablet: boolean;
    isSidebar: boolean;
    isMessage: boolean;
    setIsMessage: React.Dispatch<React.SetStateAction<boolean>>;
    errorResponse: IErrorResponse | undefined; 
    setErrorResponse: React.Dispatch<React.SetStateAction<IErrorResponse | undefined>>; 
    setSuccessMessage: React.Dispatch<React.SetStateAction<string>>; 
    payoffQuoteRequestId: string,
    user: any; // TODO: not sure need to make an interface for user soon
    proxyUser: IProxyUser | undefined;
}

interface IUnitReconveyance {
    name: string;
    paidOff: boolean;
}

const BWPayoffQuoteRequestForm = ({
    isGreaterThanMobile,
    isGreaterThanTablet,
    isSidebar,
    isMessage,
    setIsMessage,
    errorResponse,
    setErrorResponse,
    setSuccessMessage,
    payoffQuoteRequestId,
    user,
    proxyUser,
} : IBWPayoffQuoteRequestForm) => {
    const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
    const [isLoading, setIsLoading] = useState(true as boolean);
    const [invalidProps, setInvalidProps] = useState([] as any);
    const [borrowerServingLoanListData, setBorrowerServingLoanListData] = useState([] as IPayoffQuoteRequest[]);
    const [propertyUnitReconveyanceList, setPropertyUnitReconveyanceList] = useState([] as IUnitReconveyance[]);
    const [payoffQuoteRequest, setpayoffQuoteRequest] = useState({} as IPayoffQuoteRequest);

    const history = useHistory();
    const handleError = useCallback(() => {
        history.push("/error");
    }, [history]);

    const handleBackTopayoffQuoteList = useCallback(() => {
        history.push("/payoffquoterequestauto");
    }, [history]);

    const handleSelectLoan = async (value, field, obj, setObj) => {
        setPropertyUnitReconveyanceList([]);
        handleFormChange(value, field, obj, setObj);
        const selectedLoan = borrowerServingLoanListData.find(x => x.loan === value);
        if (selectedLoan && selectedLoan.loanPropertyUnitReconveyance && selectedLoan.loanPropertyUnitReconveyance.length > 0) {
            const unpaidLoanPropertyUnit = selectedLoan.loanPropertyUnitReconveyance.filter(x => x.paidOff === true);
            setPropertyUnitReconveyanceList(unpaidLoanPropertyUnit);
        } else {
            payoffQuoteRequest.payoffOption = "Loan in Full";
        }
    };
    
    const handleAddPayoffQuoteRequest = async (newPayoffQuoteRequest) => {
        setIsLoading(true);
        setInvalidProps([]);
        const notValidProperties = validateObj(newPayoffQuoteRequest, ["loan", "validThroughDate", "saleOrRefinanceOption"]);
        const propertyOfPayoffQuoteRequest = (name: keyof IPayoffQuoteRequest) => name;
        if (propertyUnitReconveyanceList && propertyUnitReconveyanceList.length > 0 && !payoffQuoteRequest.payoffOption) {
            notValidProperties.push(propertyOfPayoffQuoteRequest("payoffOption"));
        }

        if (payoffQuoteRequest.payoffOption === "Specific Property" && !payoffQuoteRequest.loanPropertyUnitReconveyance) {
            notValidProperties.push(propertyOfPayoffQuoteRequest("loanPropertyUnitReconveyance"));
        }

        if (payoffQuoteRequest.saleOrRefinanceOption === "Sale" && !payoffQuoteRequest.salePrice) {
            notValidProperties.push(propertyOfPayoffQuoteRequest("salePrice"));
        }

        if (payoffQuoteRequest.salePrice && payoffQuoteRequest.salePrice <= 0) {
            notValidProperties.push("custom-invalid-sale-price");
        }

        if (payoffQuoteRequest.emailRecipient) {
            const checkEmailFormat = payoffQuoteRequest.emailRecipient.split(",").map(email => validateEmail(email.replace(/\s/g, "")));
            if (checkEmailFormat && checkEmailFormat.includes(false)){
                notValidProperties.push("custom-invalid-email-recipient");
            }
        }

        if (notValidProperties.length === 0) {
            createPayoffQuoteRequestAsync(newPayoffQuoteRequest);
            setIsLoading(false);
        }
        else {
          setTimeout(() => { 
            setInvalidProps(notValidProperties);
          }, 50);
        }
    
        setIsLoading(false);
    };

    const createPayoffQuoteRequestAsync = async (newPayoffQuoteRequest) => {
        try {
            const response = FeatureFlags.IS_MOCK ? await createMockPayoffQuoteRequest(newPayoffQuoteRequest) : await createPayoffQuoteRequest(newPayoffQuoteRequest, proxyUser && proxyUser.id);
            if (response && response.ok) {
                setIsMessage(false);
                setSuccessMessage("You have successfully requested a new payoff quote");
                setTimeout(() => { 
                    handleBackTopayoffQuoteList();
                    setSuccessMessage("");
                }, 5000);
            }
            else {
                await handleErrorResponse(response, setIsMessage, setErrorResponse);
            }
        }
        catch (error) {
            setIsMessage(false);
        }
    };

    useEffect(() => {
        const getBorrowerServicingLoanListAsync = async () => {
            try {
                const response = FeatureFlags.IS_MOCK ? await getMockPayoffQuoteLoanList() : await getPayoffQuoteLoanList(proxyUser && proxyUser.id);
                if (response && response.ok) {
                    const borrowerLoanList = await response.json();
                    if (!borrowerLoanList) {
                        handleBackTopayoffQuoteList();
                    }
                    else {
                        const borrowerServingLoanList = borrowerLoanList.filter(x => x.status !== "serving"); // TODO: need to confirm the property name and input
                        setBorrowerServingLoanListData(borrowerServingLoanList);
                        payoffQuoteRequest["id"] = payoffQuoteRequestId;
                        setpayoffQuoteRequest({...payoffQuoteRequest});
                    }
                }
                else {
                    await handleErrorResponse(response, setIsMessage, setErrorResponse);
                }
            }
            catch (error) {
                handleError();
            }
        };

        setIsLoading(false);
        if (isFirstLoad) {
            getBorrowerServicingLoanListAsync();
        }
    }, [user, proxyUser, setIsLoading, isFirstLoad]);

    useEffect(() => {
        setIsFirstLoad(false);
        setIsLoading(false);
    }, []);

    const OrganizationFormLayout = [
        [
          {
            title: "Request Payoff Quote",
            size: 16,
            content:
                <Form className="primary-form" autoComplete="off">
                    <Grid className="vendor-form-grid"> 
                        <Grid.Row className="application-row">
                            <Grid.Column width={16}>
                                <Button
                                    icon
                                    basic
                                    floated='right'
                                    onClick={() => handleBackTopayoffQuoteList()}
                                >
                                    <div className="gmr-button-container">
                                        <Icon name='trash alternate outline' size="large" />
                                        <div className="gmr-button-icon-text">Close</div>
                                    </div>
                                </Button>
                                <Button
                                    icon
                                    basic
                                    floated='right'
                                    onClick={() => handleAddPayoffQuoteRequest(payoffQuoteRequest)}
                                >
                                    <div className="gmr-button-container">
                                        <Icon name='add' size="large" />
                                        <div className="gmr-button-icon-text">Submit</div>
                                    </div>
                                </Button>
                            </Grid.Column>  
                        </Grid.Row>
                        <Grid.Row className="application-row">
                            <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
                                <BCFormInput 
                                    labelClass="vendor-form-label"
                                    type="dropdown"
                                    label="Loan:"
                                    placeholder="Please Select"
                                    obj={payoffQuoteRequest}
                                    field="loan"
                                    dropdownOptions={getDropdownOptions(Array.from(borrowerServingLoanListData, x => x.loan))}
                                    handleFormChange={(value, field) => handleSelectLoan(value, field, payoffQuoteRequest, setpayoffQuoteRequest)}
                                    required
                                />
                                <BCValidationMessage invalidProps={invalidProps} prop="loan" propText="Please select a loan." />
                            </Grid.Column>
                            <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
                                <BCFormInput 
                                    labelClass="gmr-background-label"
                                    type="calendar"
                                    label="Valid Through Date:"
                                    obj={payoffQuoteRequest}
                                    field="validThroughDate"
                                    handleFormChange={(value, field) => handleFormChange(value, field, payoffQuoteRequest, setpayoffQuoteRequest)}
                                    required
                                />
                                <BCValidationMessage invalidProps={invalidProps} prop="validThroughDate" propText="Please select a date." />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="application-row">
                            {payoffQuoteRequest.loan && propertyUnitReconveyanceList.length > 0 ?
                                <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
                                    <BCFormInput 
                                        labelClass="vendor-form-label"
                                        horizontalRadioDisplay={true}
                                        type="radio"
                                        inputArray={["Loan in Full", "Specific Property"]} 
                                        label="Payoff Option:"
                                        obj={payoffQuoteRequest}
                                        field="payoffOption"
                                        valueField="value"
                                        handleFormChange={(value, field) => handleFormChange(value, field, payoffQuoteRequest, setpayoffQuoteRequest)}
                                        required
                                    />
                                    <BCValidationMessage invalidProps={invalidProps} prop="payoffOption" propText="Please select a payoff option." />
                                </Grid.Column> : <React.Fragment></React.Fragment>
                            }
                            {payoffQuoteRequest.payoffOption === "Specific Property" && propertyUnitReconveyanceList.length > 0 ?
                                <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
                                    <BCFormInput 
                                        labelClass="vendor-form-label"
                                        type="dropdown"
                                        label="Loan Property Unit Reconveyance:"
                                        placeholder="Please Select"
                                        obj={payoffQuoteRequest}
                                        field="loanPropertyUnitReconveyance"
                                        dropdownOptions={getDropdownOptions(Array.from(propertyUnitReconveyanceList, x => x.name))}
                                        handleFormChange={(value, field) => handleFormChange(value, field, payoffQuoteRequest, setpayoffQuoteRequest)}
                                        required
                                    />
                                    <BCValidationMessage 
                                        invalidProps={invalidProps} 
                                        prop="loanPropertyUnitReconveyance"
                                        propText="Please select a loan property unit reconveyance."
                                    />
                                </Grid.Column> :  <React.Fragment></React.Fragment>
                            }
                        </Grid.Row>
                        <Grid.Row className="application-row">
                            <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
                                <BCFormInput 
                                    labelClass="vendor-form-label"
                                    horizontalRadioDisplay={true}
                                    type="radio"
                                    inputArray={["Sale", "Refinance"]} 
                                    label="Sale Or Refinance Option:"
                                    obj={payoffQuoteRequest}
                                    field="saleOrRefinanceOption"
                                    valueField="value"
                                    handleFormChange={(value, field) => handleFormChange(value, field, payoffQuoteRequest, setpayoffQuoteRequest)}
                                    required
                                />
                                <BCValidationMessage invalidProps={invalidProps} prop="saleOrRefinanceOption" propText="Please select sale or refinance."/>
                            </Grid.Column>
                            {payoffQuoteRequest.saleOrRefinanceOption !== "Sale" ? <React.Fragment></React.Fragment> :
                                <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
                                    <BCFormInput
                                        labelClass="vendor-form-label"
                                        type="input"
                                        label="Sale Price:"
                                        placeholder="100000"
                                        obj={payoffQuoteRequest}
                                        field="salePrice"
                                        inputType="number"
                                        handleFormChange={(value, field) => handleFormChange(value, field, payoffQuoteRequest, setpayoffQuoteRequest)}
                                        required
                                    />
                                    <BCValidationMessage invalidProps={invalidProps} prop="salePrice" propText="Please enter sale price." />
                                    <BCValidationMessage invalidProps={invalidProps} prop="custom-invalid-sale-price" propText="Please enter a valid price." />
                                </Grid.Column>
                            }
                        </Grid.Row>
                        <Grid.Row className="application-row">
                            <Grid.Column width={16}>
                                <BCFormInput
                                    labelClass="vendor-form-label"
                                    type="textarea"
                                    label="Email Recipient (Additional email addresses for sending Payoff Quote, comma separated, if multiple):"
                                    placeholder="jsmith@gmail.com, mjohnson@hotmail.com"
                                    obj={payoffQuoteRequest}
                                    field="emailRecipient"
                                    handleFormChange={(value, field) => handleFormChange(value, field, payoffQuoteRequest, setpayoffQuoteRequest)}
                                />
                                    <BCValidationMessage invalidProps={invalidProps} prop="custom-invalid-email-recipient" propText="Contain invalid email." />
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Form>
            }
        ]
    ];

    return (
        <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
            {isLoading ? <Loader active inline="centered" size="big" /> : <React.Fragment>
                <BCMessage
                    animation={"fly down"}
                    duration={750}
                    visible={isMessage}
                    transitionOnMount
                    styles="full-width"
                    onDismiss={() => setIsMessage(false)}
                    errorResponse={errorResponse}
                />
                <BCDashboard
                    DashboardLayout={OrganizationFormLayout}
                    isGreaterThanMobile={isGreaterThanMobile}
                />
            </React.Fragment>}
        </div>
    );
};

export default BWPayoffQuoteRequestForm;